import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import {
  MapPin, Star, Flag, Waves, Calendar, Clock, Users, ArrowRight, Ticket, Buildings,
} from "@phosphor-icons/react";

const TABS = [
  { id: "tee-times", label: "Tee Times", icon: Flag },
  { id: "resorts", label: "Resorts", icon: Buildings },
];

export default function Booking() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") === "resorts" ? "resorts" : "tee-times";
  return (
    <div className="space-y-6">
      <div className="rise">
        <span className="label-eyebrow">Plan your trip</span>
        <h1 className="font-heading font-black text-4xl sm:text-5xl tracking-tighter mt-1">Book your Fiji golf escape</h1>
        <p className="text-[#0F2537]/70 mt-2 max-w-2xl">Lock in tee times at championship courses, then settle into one of Fiji's finest golf resorts — all in one place.</p>
      </div>

      <div className="flex items-center gap-2 rise rise-delay-1">
        {TABS.map((t) => {
          const Icon = t.icon;
          const active = tab === t.id;
          return (
            <button
              key={t.id}
              onClick={() => setParams({ tab: t.id })}
              data-testid={`booking-tab-${t.id}`}
              className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-bold transition ${
                active ? "bg-[#0F2537] text-white" : "bg-white border border-[#E2E0D8] text-[#0F2537] hover:bg-black/5"
              }`}
            >
              <Icon size={16} weight={active ? "fill" : "regular"} />
              {t.label}
            </button>
          );
        })}
        <Link
          to="/bookings"
          data-testid="my-bookings-link"
          className="ml-auto inline-flex items-center gap-1 text-sm font-bold text-[#E05D3A] hover:underline"
        >
          My bookings <ArrowRight size={14} weight="bold" />
        </Link>
      </div>

      {tab === "tee-times" ? <TeeTimesPanel /> : <ResortsPanel />}
    </div>
  );
}

// ===== Tee Times =====

function TeeTimesPanel() {
  const [courses, setCourses] = useState([]);
  const tomorrow = useMemo(() => {
    const d = new Date(); d.setDate(d.getDate() + 1);
    return d.toISOString().slice(0, 10);
  }, []);
  const [courseId, setCourseId] = useState("");
  const [date, setDate] = useState(tomorrow);
  const [slots, setSlots] = useState(null);
  const [players, setPlayers] = useState(2);
  const [busy, setBusy] = useState(false);
  const [chosenSlot, setChosenSlot] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/courses").then((r) => {
      setCourses(r.data);
      if (r.data.length) setCourseId(r.data[0].id);
    });
  }, []);

  useEffect(() => {
    if (!courseId || !date) return;
    setSlots(null);
    api.get(`/courses/${courseId}/tee-times`, { params: { date } })
      .then((r) => setSlots(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)));
  }, [courseId, date]);

  const book = async () => {
    if (!chosenSlot) return;
    setBusy(true);
    try {
      const r = await api.post("/bookings/tee-time", {
        course_id: courseId, tee_date: date, slot: chosenSlot, players,
      });
      toast.success(`Tee time confirmed at ${r.data.slot} · FJ$${r.data.total_fjd.toLocaleString()}`);
      navigate("/bookings");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-5 rise rise-delay-2">
      <div className="card-default p-5 lg:col-span-2 space-y-4">
        <div className="grid sm:grid-cols-3 gap-3">
          <Field label="Course">
            <select
              value={courseId} onChange={(e) => { setCourseId(e.target.value); setChosenSlot(null); }}
              data-testid="tee-time-course-select"
              className="w-full rounded-lg border border-[#E2E0D8] bg-white px-3 py-2 text-sm font-semibold"
            >
              {courses.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </Field>
          <Field label="Date">
            <input
              type="date" value={date} min={tomorrow}
              onChange={(e) => { setDate(e.target.value); setChosenSlot(null); }}
              data-testid="tee-time-date-input"
              className="w-full rounded-lg border border-[#E2E0D8] bg-white px-3 py-2 text-sm font-semibold"
            />
          </Field>
          <Field label="Players">
            <select
              value={players} onChange={(e) => setPlayers(Number(e.target.value))}
              data-testid="tee-time-players-select"
              className="w-full rounded-lg border border-[#E2E0D8] bg-white px-3 py-2 text-sm font-semibold"
            >
              {[1, 2, 3, 4].map((n) => <option key={n} value={n}>{n} player{n > 1 ? "s" : ""}</option>)}
            </select>
          </Field>
        </div>

        {slots ? (
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="label-eyebrow">Available slots</span>
              <span className="text-xs font-bold text-[#0F2537]/70">FJ${slots.green_fee_fjd}/player incl. cart</span>
            </div>
            <div className="grid grid-cols-4 sm:grid-cols-6 md:grid-cols-8 gap-2 max-h-[420px] overflow-y-auto pr-1">
              {slots.slots.map((s) => {
                const disabled = !s.available && !s.booked_by_me;
                const chosen = chosenSlot === s.slot;
                return (
                  <button
                    key={s.slot}
                    disabled={disabled}
                    onClick={() => setChosenSlot(s.slot)}
                    data-testid={`slot-${s.slot}`}
                    title={s.booked_by_me ? "Your existing booking" : (disabled ? "Already booked" : "")}
                    className={`px-2 py-2 rounded-lg text-xs font-bold border transition ${
                      chosen ? "bg-[#0F2537] text-white border-[#0F2537]" :
                      s.booked_by_me ? "bg-[#E05D3A]/10 text-[#E05D3A] border-[#E05D3A]/30" :
                      disabled ? "bg-black/5 text-[#0F2537]/30 border-transparent cursor-not-allowed line-through" :
                      "bg-white text-[#0F2537] border-[#E2E0D8] hover:border-[#0F2537]"
                    }`}
                  >
                    {s.slot}
                  </button>
                );
              })}
            </div>
          </div>
        ) : (
          <div className="text-sm text-[#0F2537]/60">Loading slots…</div>
        )}
      </div>

      <div className="card-default p-5 space-y-4 h-fit sticky top-24">
        <div className="label-eyebrow">Your booking</div>
        <Summary icon={Flag} label="Course" value={courses.find((c) => c.id === courseId)?.name || "—"} />
        <Summary icon={Calendar} label="Date" value={date} />
        <Summary icon={Clock} label="Tee time" value={chosenSlot || "Pick a slot"} />
        <Summary icon={Users} label="Players" value={`${players} player${players > 1 ? "s" : ""}`} />
        <div className="border-t border-[#E2E0D8] pt-3 flex items-end justify-between">
          <span className="label-eyebrow">Total</span>
          <span className="font-heading text-2xl font-black">
            FJ${slots ? (slots.green_fee_fjd * players).toLocaleString() : "—"}
          </span>
        </div>
        <button
          onClick={book} disabled={!chosenSlot || busy}
          data-testid="confirm-tee-time-btn"
          className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-[#E05D3A] text-white text-sm font-bold disabled:opacity-50 hover:brightness-110 transition"
        >
          <Ticket size={16} weight="fill" /> {busy ? "Booking…" : "Confirm tee time"}
        </button>
        <p className="text-[10px] text-[#0F2537]/50 leading-relaxed">
          Pay at the pro shop on arrival. Free cancellation up to 24 hours before tee-off.
        </p>
      </div>
    </div>
  );
}

// ===== Resorts =====

function ResortsPanel() {
  const [resorts, setResorts] = useState([]);
  useEffect(() => { api.get("/resorts").then((r) => setResorts(r.data)); }, []);
  return (
    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 rise rise-delay-2">
      {resorts.map((r, i) => (
        <Link
          key={r.id} to={`/resorts/${r.id}`} data-testid={`resort-card-${r.id}`}
          className="card-interactive overflow-hidden group rise" style={{ animationDelay: `${i * 50}ms` }}
        >
          <div className="aspect-[16/10] overflow-hidden bg-[#0F2537]/10 relative">
            {r.image_url && <img src={r.image_url} alt={r.name} className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500" />}
            <div className="absolute top-3 right-3 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-white/90 backdrop-blur text-xs font-bold text-[#0F2537]">
              <Star size={12} weight="fill" className="text-[#E05D3A]" /> {r.rating}
            </div>
          </div>
          <div className="p-5">
            <div className="flex items-center gap-1 text-xs font-semibold text-[#0F2537]/60">
              <MapPin size={12} weight="fill" /> {r.location}
            </div>
            <h3 className="font-heading font-bold text-lg mt-1 leading-tight">{r.name}</h3>
            <p className="text-sm text-[#0F2537]/70 mt-2 line-clamp-2">{r.description}</p>
            <div className="flex items-center justify-between mt-4">
              <div>
                <div className="text-[10px] font-bold text-[#0F2537]/50 uppercase tracking-wider">From</div>
                <div className="font-heading text-xl font-black">FJ${r.nightly_rate_fjd.toLocaleString()}<span className="text-xs font-semibold text-[#0F2537]/60">/night</span></div>
              </div>
              <span className="inline-flex items-center gap-1 text-sm font-bold text-[#E05D3A]">
                Book <ArrowRight size={14} weight="bold" />
              </span>
            </div>
          </div>
        </Link>
      ))}
    </div>
  );
}

// ===== Bits =====

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="label-eyebrow">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Summary({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-9 h-9 rounded-lg bg-[#0F2537]/5 flex items-center justify-center text-[#0F2537]/80 shrink-0">
        <Icon size={18} weight="fill" />
      </div>
      <div className="min-w-0">
        <div className="text-[10px] font-bold uppercase tracking-wider text-[#0F2537]/50">{label}</div>
        <div className="text-sm font-bold text-[#0F2537] truncate">{value}</div>
      </div>
    </div>
  );
}
